/**
 * Reine Logik der Admin-Oberfläche „Rollen & Rechte" (docs/rollensystem-plan.md
 * §6.1): Matrixzeilen nach Gruppen, Rollen-Chips je Person, Firmenauswahl des
 * Super-Admins, Protokolltext und die optimistischen Kopien des Stands.
 *
 * Kein React, kein Supabase — `RollenRechteView`, `AdminUserManagement` und
 * `BenutzerRollenChips` rendern nur, was hier entschieden wird. Aus
 * `rollenVerwaltung` deshalb NUR Typen: ein Wertimport zöge den
 * Supabase-Client in die Tests.
 */
import { RECHTE, RECHT_GRUPPEN, type RechtGruppe, type RechtInfo, type RechtSchluessel } from '../../services/rechte/katalog';
import type { ProtokollZeile, RolleZeile, RollenStand } from '../../services/rechte/rollenVerwaltung';
import type { UserProfile } from '../../types';

/** Was die Logik von einem Profil braucht — Tests kommen ohne volles Profil aus. */
export type Person = Pick<UserProfile, 'id' | 'role' | 'email' | 'first_name' | 'last_name' | 'company_id' | 'company_name'>;

const idOf = (v?: string | null): string => (v || '').trim();

export const personName = (u?: Partial<Person> | null): string =>
    `${u?.first_name || ''} ${u?.last_name || ''}`.trim() || u?.email || 'unbekannt';

/** Admins stehen nicht in der Matrix: sie haben immer alle Rechte (§3.1). */
export const istAdminRolle = (role?: string | null): boolean => role === 'admin' || role === 'super_admin';

export const fehlerText = (e: unknown): string =>
    (e as { message?: string } | null)?.message || String(e);

/** Stand für eine Firma, deren Rollen nicht lesbar sind — Matrix und Knöpfe aus. */
export const standNichtVerfuegbar = (companyId: string | null, grund: string): RollenStand => ({
    verfuegbar: false,
    grund,
    companyId,
    rollen: [],
    matrix: new Map(),
    benutzerRollen: new Map(),
});

// ─── Matrix ──────────────────────────────────────────────────────────────────

export interface MatrixGruppe {
    schluessel: RechtGruppe;
    titel: string;
    rechte: RechtInfo[];
}

/** Zeilen der Matrix: Gruppen in Katalogreihenfolge, leere Gruppen fallen weg. */
export const gruppiereRechte = (
    rechte: readonly RechtInfo[] = RECHTE,
    gruppen: readonly { schluessel: RechtGruppe; titel: string }[] = RECHT_GRUPPEN,
): MatrixGruppe[] =>
    gruppen
        .map(g => ({ schluessel: g.schluessel, titel: g.titel, rechte: rechte.filter(r => r.gruppe === g.schluessel) }))
        .filter(g => g.rechte.length > 0);

/** Schlüssel einer Matrixzelle — für die Sperre gegen den Doppelklick. */
export const zellSchluessel = (rolle: string, recht: string): string => `${rolle}|${recht}`;

export const hatRolleRecht = (stand: RollenStand | null | undefined, rolle: string, recht: RechtSchluessel): boolean =>
    !!stand?.matrix.get(rolle)?.has(recht);

export const rechtTitel = (recht: string | null | undefined): string =>
    RECHTE.find(r => r.schluessel === recht)?.titel || 'unbekannt';

export const rolleName = (rollen: readonly RolleZeile[], schluessel: string | null | undefined): string =>
    rollen.find(r => r.schluessel === schluessel)?.name || 'unbekannt';

/** Kopie der Matrix mit bzw. ohne das Recht an der Rolle — nie an Ort und Stelle. */
export const matrixMit = (
    matrix: ReadonlyMap<string, ReadonlySet<RechtSchluessel>>,
    rolle: string,
    recht: RechtSchluessel,
    erlaubt: boolean,
): Map<string, Set<RechtSchluessel>> => {
    const neu = new Map<string, Set<RechtSchluessel>>();
    for (const [k, v] of matrix) neu.set(k, new Set(v));
    const zeile = neu.get(rolle) || new Set<RechtSchluessel>();
    if (erlaubt) zeile.add(recht); else zeile.delete(recht);
    neu.set(rolle, zeile);
    return neu;
};

export const standMitRecht = (stand: RollenStand, rolle: string, recht: RechtSchluessel, erlaubt: boolean): RollenStand =>
    ({ ...stand, matrix: matrixMit(stand.matrix, rolle, recht, erlaubt) });

/** Wie viele Personen tragen die Rolle? Eine getragene Rolle lässt sich nicht löschen. */
export const traegerAnzahl = (stand: RollenStand | null | undefined, rolle: string): number => {
    if (!stand) return 0;
    let n = 0;
    for (const liste of stand.benutzerRollen.values()) if (liste.includes(rolle)) n++;
    return n;
};

/** Warum eine Rolle nicht löschbar ist — `null` heißt: löschbar. */
export const loeschSperre = (stand: RollenStand | null | undefined, rolle: RolleZeile): string | null => {
    if (rolle.system) return 'Startrollen lassen sich nicht löschen.';
    const n = traegerAnzahl(stand, rolle.schluessel);
    if (n > 0) return `${n === 1 ? 'Eine Person trägt' : `${n} Personen tragen`} diese Rolle — erst in der Benutzerverwaltung entfernen.`;
    return null;
};

/**
 * Kürzer als die Datenbank erlaubt (80, `rollen_name_check`): der Name steht
 * als Spaltenkopf der Matrix. Die Beschreibung darf so lang sein wie dort.
 */
export const ROLLENNAME_MAX = 40;
export const BESCHREIBUNG_MAX = 500;

/** Eingabe „Rolle anlegen" prüfen — `null` heißt: in Ordnung. */
export const pruefeRollenName = (name: string, rollen: readonly RolleZeile[]): string | null => {
    const n = name.trim().replace(/\s+/g, ' ');
    if (!n) return 'Bitte einen Namen eingeben.';
    if (n.length > ROLLENNAME_MAX) return `Der Name darf höchstens ${ROLLENNAME_MAX} Zeichen haben.`;
    if (rollen.some(r => r.name.trim().toLowerCase() === n.toLowerCase())) return `Eine Rolle „${n}" gibt es schon.`;
    return null;
};

// ─── Rollen je Person ────────────────────────────────────────────────────────

export interface RolleChip {
    schluessel: string;
    name: string;
    beschreibung: string;
}

/**
 * Chips der vergebenen Rollen (in der Reihenfolge der Firma) und die noch
 * wählbaren Rollen. Ein Schlüssel, den die Firma nicht (mehr) kennt, bleibt
 * als Chip stehen — sonst ließe er sich nie entfernen.
 */
export const rollenChips = (
    rollen: readonly RolleZeile[],
    vergeben: readonly string[],
): { chips: RolleChip[]; optionen: RolleZeile[] } => {
    const menge = new Set(vergeben);
    const bekannt = new Set(rollen.map(r => r.schluessel));
    const chips: RolleChip[] = rollen
        .filter(r => menge.has(r.schluessel))
        .map(r => ({ schluessel: r.schluessel, name: r.name, beschreibung: r.beschreibung }));
    for (const s of menge) {
        if (!bekannt.has(s)) chips.push({ schluessel: s, name: s, beschreibung: 'Diese Rolle steht nicht (mehr) in der Liste der Firma.' });
    }
    return { chips, optionen: rollen.filter(r => !menge.has(r.schluessel)) };
};

/** Kopie der Rollen je Person mit bzw. ohne die Rolle — nie an Ort und Stelle. */
export const benutzerRollenMit = (
    benutzerRollen: ReadonlyMap<string, readonly string[]>,
    userId: string,
    rolle: string,
    an: boolean,
): Map<string, string[]> => {
    const neu = new Map<string, string[]>();
    for (const [k, v] of benutzerRollen) neu.set(k, [...v]);
    const ohne = (neu.get(userId) || []).filter(r => r !== rolle);
    const liste = an ? [...ohne, rolle] : ohne;
    if (liste.length > 0) neu.set(userId, liste); else neu.delete(userId);
    return neu;
};

export const standMitBenutzerRolle = (stand: RollenStand, userId: string, rolle: string, an: boolean): RollenStand =>
    ({ ...stand, benutzerRollen: benutzerRollenMit(stand.benutzerRollen, userId, rolle, an) });

/** Kopie der Stände je Firma, eine Firma ersetzt. Fehlt sie, bleibt alles, wie es ist. */
export const staendeMit = (
    staende: ReadonlyMap<string, RollenStand>,
    companyId: string,
    aendern: (s: RollenStand) => RollenStand,
): Map<string, RollenStand> => {
    const neu = new Map(staende);
    const alt = neu.get(companyId);
    if (alt) neu.set(companyId, aendern(alt));
    return neu;
};

/** Was die Spalte „Rollen" der Benutzerverwaltung für eine Person zeigt. */
export type RollenSpalte =
    | { art: 'alle' }
    | { art: 'freelancer' }
    | { art: 'ohne_firma' }
    | { art: 'laedt' }
    | { art: 'aus'; grund: string }
    | { art: 'chips'; companyId: string; chips: RolleChip[]; optionen: RolleZeile[] };

/**
 * `zuLaden` = die Firmen, deren Stand überhaupt geladen wird
 * (`firmenZumLaden`). Eine Person außerhalb davon stünde sonst für immer auf
 * „lädt" — z. B. beim Admin ohne eigene `company_id`.
 */
export const rollenSpalte = (
    u: Person,
    staende: ReadonlyMap<string, RollenStand>,
    zuLaden?: readonly string[],
): RollenSpalte => {
    if (istAdminRolle(u.role)) return { art: 'alle' };
    if (u.role === 'freelancer') return { art: 'freelancer' };
    const companyId = idOf(u.company_id);
    if (!companyId) return { art: 'ohne_firma' };
    const stand = staende.get(companyId);
    if (!stand && zuLaden && !zuLaden.includes(companyId)) {
        return { art: 'aus', grund: 'Rollen dieser Firma werden hier nicht geladen — ein Admin pflegt nur die eigene Firma.' };
    }
    if (!stand) return { art: 'laedt' };
    if (!stand.verfuegbar) return { art: 'aus', grund: stand.grund || 'Rollen sind nicht verfügbar.' };
    const { chips, optionen } = rollenChips(stand.rollen, stand.benutzerRollen.get(u.id) || []);
    return { art: 'chips', companyId, chips, optionen };
};

// ─── Firmen ──────────────────────────────────────────────────────────────────

export interface FirmaOption {
    id: string;
    name: string;
}

/**
 * Firmen, deren Matrix ein Super-Admin wählen kann: eindeutig nach
 * `company_id`, nach Name sortiert. Freelancer zählen nicht — eine Firma nur
 * aus Freelancern hat keine Rollen zu pflegen. Die eigene Firma ist immer da.
 */
export const firmenAuswahl = (users: readonly Person[], eigene?: Person | null): FirmaOption[] => {
    const namen = new Map<string, string>();
    const merke = (u: Person) => {
        const id = idOf(u.company_id);
        if (!id) return;
        const name = (u.company_name || '').trim();
        if (!namen.get(id)) namen.set(id, name);
    };
    if (eigene) merke(eigene);
    for (const u of users) if (u.role !== 'freelancer') merke(u);
    return [...namen]
        .map(([id, name]) => ({ id, name: name || `Firma ${id}` }))
        .sort((a, b) => a.name.localeCompare(b.name, 'de') || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
};

/**
 * Welche Firma die Ansicht zeigt. Admin: immer die eigene. Super-Admin: die
 * gewählte, solange es sie gibt, sonst die eigene, sonst die erste.
 */
export const wirksameFirma = (
    ich: Person | null | undefined,
    firmen: readonly FirmaOption[],
    gewaehlt: string | null,
): string | null => {
    const eigene = idOf(ich?.company_id);
    if (ich?.role !== 'super_admin') return eigene || null;
    if (gewaehlt && firmen.some(f => f.id === gewaehlt)) return gewaehlt;
    if (eigene) return eigene;
    return firmen.length > 0 ? firmen[0].id : null;
};

/**
 * Firmen, deren Stand die Benutzerverwaltung lädt — je Firma einmal. Admin:
 * nur die eigene. Super-Admin: jede Firma, in der eine Person Rollen tragen
 * kann (Admins und Freelancer zeigen keine Chips). Sortiert, damit der
 * Schlüssel der Ladeliste stabil bleibt.
 */
export const firmenZumLaden = (users: readonly Person[], ich: Person | null | undefined): string[] => {
    if (!ich) return [];
    if (ich.role !== 'super_admin') {
        const id = idOf(ich.company_id);
        return id ? [id] : [];
    }
    const ids = new Set<string>();
    for (const u of users) {
        if (istAdminRolle(u.role) || u.role === 'freelancer') continue;
        const id = idOf(u.company_id);
        if (id) ids.add(id);
    }
    return [...ids].sort();
};

// ─── Protokoll ───────────────────────────────────────────────────────────────

export interface ProtokollKontext {
    users: readonly Person[];
    rollen: readonly RolleZeile[];
}

export interface ProtokollTeile {
    zeit: string;
    wer: string;
    was: string;
}

const zwei = (n: number): string => String(n).padStart(2, '0');

/** „12.10. 18:04" in Ortszeit; Unlesbares bleibt, wie es kam. */
export const protokollZeit = (am: string): string => {
    const d = new Date(am);
    if (!am || isNaN(d.getTime())) return am || '—';
    return `${zwei(d.getDate())}.${zwei(d.getMonth() + 1)}. ${zwei(d.getHours())}:${zwei(d.getMinutes())}`;
};

/**
 * Eine Protokollzeile lesbar. Namen kommen aus den Profilen, Rollennamen aus
 * dem Stand (eine gelöschte Rolle über den mitgeschriebenen Namen), Rechte aus
 * dem Katalog; was sich nicht auflösen lässt, heißt „unbekannt". Ohne `von`
 * war es die Einrichtung selbst („System").
 */
export const protokollTeile = (z: ProtokollZeile, k: ProtokollKontext): ProtokollTeile => {
    const person = (id: string | null): string => {
        const u = id ? k.users.find(x => x.id === id) : undefined;
        return u ? personName(u) : 'unbekannt';
    };
    const rolle = (): string => k.rollen.find(r => r.schluessel === z.rolle)?.name || z.name || 'unbekannt';
    const wer = z.von ? person(z.von) : 'System';
    let was: string;
    switch (z.art) {
        case 'recht':
            was = `${rolle()} → ${rechtTitel(z.recht)} ${z.wert === true ? 'an' : z.wert === false ? 'aus' : 'geändert'}`;
            break;
        case 'benutzer_rolle':
            was = z.wert === false
                ? `${person(z.userId)}: Rolle ${rolle()} entfernt`
                : `${person(z.userId)} bekommt Rolle ${rolle()}`;
            break;
        case 'rolle_angelegt':
            was = `Rolle „${z.name || rolle()}" angelegt`;
            break;
        case 'rolle_geloescht':
            was = `Rolle „${z.name || rolle()}" gelöscht`;
            break;
        case 'einrichtung':
            // Die Einrichtung schreibt ihren Klartext in `name` (Nr. 149).
            was = z.name || 'Startrollen und Standard-Matrix eingerichtet';
            break;
        default:
            was = 'unbekannte Änderung';
    }
    return { zeit: protokollZeit(z.am), wer, was };
};

export const protokollText = (z: ProtokollZeile, k: ProtokollKontext): string => {
    const t = protokollTeile(z, k);
    return `${t.zeit} · ${t.wer}: ${t.was}`;
};
